import { DEFAULT_LIMIT_PER_PAGE } from "@/constants";
import { profileServiceRequest, skip } from "./utils";

/**
 * @typedef {Object} ProfileUserType
 * @property {"Participant"|"Api Employee"|"Company"|"University"} value - The user type.
 */

/**
 * @typedef {Object} ParentGuardian
 * @property {string} id - UUID of the guardian.
 * @property {string} profile_id - UUID of the associated profile.
 * @property {string} name - Name of the guardian.
 * @property {string} email - Email address.
 * @property {string} [phone] - Optional phone number.
 * @property {boolean} [is_emergency_contact=false] - Indicates if the guardian is an emergency contact.
 * @property {boolean} [cc_parent_preference=false] - Indicates if the guardian should be CC'd.
 * @property {string} [archived_at] - Timestamp of archival.
 * @property {string} [created_at] - Timestamp of creation.
 * @property {string} [updated_at] - Timestamp of last update.
 * @property {string} [updated_by] - User who last updated.
 */

/**
 * @typedef {Object} ProfileMajorMinor
 * @property {string} cip_code - CIP code.
 * @property {string} id - UUID of the major/minor.
 * @property {string} profile_id - UUID of the associated profile.
 * @property {string} [archived_at] - Timestamp of archival.
 * @property {string} [created_at] - Timestamp of creation.
 * @property {string} [updated_at] - Timestamp of last update.
 * @property {string} [updated_by] - User who last updated.
 */

/**
 * @typedef {Object} ProfileCollege
 * @property {string} college_id - College identifier.
 * @property {string} id - UUID of the college entry.
 * @property {string} [archived_at] - Timestamp of archival.
 * @property {string} [created_at] - Timestamp of creation.
 * @property {string} [updated_at] - Timestamp of last update.
 * @property {string} [updated_by] - User who last updated.
 */

/**
 * @typedef {Object} Profile
 * @property {string} [id] - UUID of the profile.
 * @property {string} okta_id - Okta ID.
 * @property {string} [archived_at] - Timestamp of archival.
 * @property {string} [created_at] - Timestamp of creation.
 * @property {string} [updated_at] - Timestamp of last update.
 * @property {string} [updated_by] - User who last updated.
 * @property {string} [legal_first_name] - Legal first name.
 * @property {string} email - Email address.
 * @property {string} [last_name] - Last name.
 * @property {string} [phone_number] - Phone number.
 * @property {string} [full_name] - Full name.
 * @property {ParentGuardian[]} [parents] - List of parents/guardians.
 * @property {ProfileMajorMinor[]} majors - List of majors.
 * @property {ProfileMajorMinor[]} minors - List of minors.
 * @property {ProfileCollege[]} colleges - List of colleges.
 * @property {ProfileUserType[]} [user_types] - List of user types.
 * @property {string} [preferred_first_name] - Preferred first name.
 * @property {boolean} [dynamic_university=false] - Indicates if dynamic university is enabled.
 * @property {boolean} [hide_from_approval_list=false] - Indicates if hidden from approval list.
 * @property {string} [birthdate] - Birthdate.
 * @property {boolean} [terms_and_eula_acceptance=false] - Indicates terms acceptance.
 * @property {string} [gender_identity] - Gender identity.
 * @property {boolean} [dual_citizenship=false] - Indicates dual citizenship.
 * @property {number} [cumulative_gpa] - Cumulative GPA.
 * @property {boolean} [needs_advising=false] - Indicates if advising is needed.
 * @property {boolean} [requires_accommodations=false] - Indicates if accommodations are needed.
 * @property {boolean} [advisory_flag=false] - Advisory flag status.
 */

/**
 * @typedef {import("@/providers/impersonation/types").ResolvedImpersonation} ResolvedImpersonation
 */

const ProfileTrackingModel = {
  utm_id: "",
  utm_campaign: "",
  utm_content: "",
  utm_medium: "",
  utm_source: "",
  utm_term: "",
};

const profileService = {
  async getProfiles({ limit = 10, page = 1, q = "", extraParams = {} }) {
    return await profileServiceRequest({
      method: "get",
      url: "/profiles",
      params: {
        limit,
        skip: skip(page, limit),
        q,
        ...extraParams,
      },
      failureMessage: "Error loading Profile data",
    });
  },
  async getLimitedProfiles({ extraParams = {} }) {
    return await profileServiceRequest({
      method: "get",
      url: "/profiles/limited/",
      params: {
        ...extraParams,
      },
      failureMessage: "Error loading Profile data",
    });
  },
  async getProfile(oktaId) {
    return await profileServiceRequest({
      method: "get",
      url: `/profiles/details/${oktaId}`,
      failureMessage: "Error loading Profile data",
    });
  },
  async createProfile(payload) {
    return await profileServiceRequest({
      method: "post",
      url: "/profiles/create",
      payload: payload,
    });
  },
  async updateProfile(oktaId, payload) {
    return await profileServiceRequest({
      method: "put",
      url: `/profiles/update/${oktaId}`,
      payload: payload,
    });
  },
  async updatePartiallyProfile(profileId, payload) {
    return await profileServiceRequest({
      method: "patch",
      url: `/profiles/update/${profileId}`,
      payload: payload,
    });
  },
  async getFinancialAid(applicationId, profileId) {
    return await profileServiceRequest({
      method: "get",
      url: `/profiles/financial_aid/${applicationId}/${profileId}`,
    });
  },
  async createFinancialAid(payload) {
    const id = payload?.okta_id ?? payload?.profile_id;
    return await profileServiceRequest({
      method: "post",
      url: `/profiles/financial_aid/${id}`,
      payload: {
        ...payload,
        profile_id: id,
      },
    });
  },
  async toggleFinancialAid(applicationId, payload) {
    const id = payload?.okta_id ?? payload?.profile_id;
    return await profileServiceRequest({
      method: "put",
      url: `/profiles/financial_aid/${applicationId}/${id}`,
      payload: {
        ...payload,
        profile_id: id,
      },
    });
  },
  async getSubmissionsByOrder(orderId, extraParams = {}) {
    return await profileServiceRequest({
      method: "get",
      url: `/onboarding/submissions/order/${orderId}`,
      params: {
        ...extraParams,
      },
    });
  },
  async getSubmissionsById(submissionId) {
    return await profileServiceRequest({
      method: "get",
      url: `/onboarding/submissions/${submissionId}`,
    });
  },
  async createSubmission(payload) {
    return await profileServiceRequest({
      method: "post",
      url: `/onboarding/submissions`,
      payload: payload,
    });
  },
  async updateSubmission(questionnaireId, payload) {
    return await profileServiceRequest({
      method: "put",
      url: `/onboarding/submissions/${questionnaireId}`,
      payload: payload,
    });
  },
  async upsertSubmission(payload) {
    return await profileServiceRequest({
      method: "put",
      url: `/submissions`,
      payload: payload,
      apiVersion: "v2",
    });
  },
  async getSubmissions({ limit = DEFAULT_LIMIT_PER_PAGE, page = 1, ...params }) {
    return await profileServiceRequest({
      method: "get",
      url: `/submissions`,
      params: {
        limit,
        skip: skip(page, limit),
        ...params,
      },
      apiVersion: "v2",
    });
  },
  async updateSubmissionFeedback(submissionId, payload) {
    return await profileServiceRequest({
      method: "patch",
      url: `/submissions/${submissionId}/feedback`,
      payload: payload,
      apiVersion: "v2",
    });
  },
  async upsertTodoStatusOverride(applicationId, todoId, status) {
    return await profileServiceRequest({
      method: "put",
      url: `/applications/${applicationId}/todos/${todoId}/status_override/`,
      payload: { status },
      apiVersion: "v1",
    });
  },
  async listTodoComments(applicationId, todoId) {
    return await profileServiceRequest({
      method: "get",
      url: `/applications/${applicationId}/todos/${todoId}/comments/`,
      apiVersion: "v1",
    });
  },
  async upsertTodoComment(applicationId, todoId, payload) {
    return await profileServiceRequest({
      method: "put",
      url: `/applications/${applicationId}/todos/${todoId}/comments/`,
      payload: payload,
      apiVersion: "v1",
    });
  },
  async deleteTodoComment(applicationId, todoId, commentId) {
    return await profileServiceRequest({
      method: "delete",
      url: `/applications/${applicationId}/todos/${todoId}/comments/${commentId}/`,
      apiVersion: "v1",
    });
  },
  async updateTodosSubmissions(todoSubmissionsId, payload) {
    return await profileServiceRequest({
      method: "put",
      url: `/onboarding/todos_submissions/${todoSubmissionsId}`,
      payload: payload,
    });
  },
  async getTodosSubmissions(params) {
    return await profileServiceRequest({
      method: "get",
      url: `/onboarding/todos_submissions`,
      params: params,
    });
  },
  async getDocumentPreSignedUrl({ ...params }) {
    return await profileServiceRequest({
      method: "post",
      url: "/files/pre_signed_url/",
      payload: {
        method_name: "get_object",
        ...params,
      },
      failureMessage: "Error requesting Pre Signed url",
    });
  },
  async getOnboardingRequiredTodos(oktaId, applicationId) {
    return await profileServiceRequest({
      method: "get",
      url: `/onboarding/required_todos/${oktaId}/${applicationId}`,
    });
  },
  async getProfileComments({ limit = DEFAULT_LIMIT_PER_PAGE, page = 1, q = "", extraParams = {}, skipErrorBus }) {
    return await profileServiceRequest({
      method: "get",
      url: `/profiles/comments/`,
      params: {
        limit,
        skip: Math.max(skip(page, limit), 0),
        q,
        ...extraParams,
      },
      skipErrorBus,
      failureMessage: "Error loading profile comments data",
    });
  },
  async createProfileComment({ payload = {}, skipErrorBus }) {
    return await profileServiceRequest({
      method: "post",
      url: "/profiles/comments",
      payload: payload,
      skipErrorBus,
    });
  },
  async updateProfileComment({ payload = {}, skipErrorBus }) {
    return await profileServiceRequest({
      method: "put",
      url: `/profiles/comments`,
      payload: payload,
      skipErrorBus,
    });
  },
  async getProfileDevices({ limit = 10, page = 1, extraParams = {} }) {
    return await profileServiceRequest({
      method: "get",
      url: "/profiles/devices",
      params: {
        limit,
        skip: skip(page, limit),
        ...extraParams,
      },
      failureMessage: "Error loading Profile devices data",
    });
  },
  async createSubmissionsPlatform({ payload = {}, skipErrorBus }) {
    return await profileServiceRequest({
      method: "post",
      url: `/onboarding/platform`,
      payload: payload,
      skipErrorBus,
    });
  },
  /**
   * Add UTM Marketing tracking to a Profile
   *
   * @param {string} oktaId Users Okta Id
   * @param {ProfileTrackingModel} payload UTM Tracking Information
   * @param {boolean} skipErrorBus
   * @returns
   */
  async addProfileTracking(oktaId = "", payload = ProfileTrackingModel, skipErrorBus = false) {
    return await profileServiceRequest({
      method: "post",
      url: `/profiles/track/${oktaId}`,
      payload: payload,
      skipErrorBus: skipErrorBus,
    });
  },
  async getMatchedMates(payload) {
    return await profileServiceRequest({
      method: "post",
      url: `/profiles/matching_mates`,
      payload: payload,
    });
  },
  async getOrientations({ limit = 10, page = 1, extraParams = {} }) {
    return await profileServiceRequest({
      method: "get",
      url: "/orientations",
      params: {
        limit,
        skip: skip(page, limit),
        ...extraParams,
      },
      failureMessage: "Error loading Orientation data",
    });
  },
  async updateOrientationStatus(participantId, profileOrientations) {
    return await profileServiceRequest({
      method: "put",
      url: `/orientations/${participantId}`,
      payload: {
        profile_orientations: profileOrientations,
      },
      failureMessage: "Error updating Orientation status",
    });
  },
  async createOrientation(participantId, orientationIds, orderId) {
    return await profileServiceRequest({
      method: "post",
      url: `/orientations/${participantId}`,
      payload: {
        orientation_ids: orientationIds,
        order_id: orderId,
      },
      failureMessage: "Error creating Orientation",
    });
  },
  async listProfileOrientations(participantId, orderIds) {
    return await profileServiceRequest({
      method: "put",
      url: `/orientations/profiles/${participantId}`,
      payload: {
        orders_ids: orderIds,
      },
      failureMessage: "Error loading Profile Orientations",
    });
  },
  async listMyStudentsOrientations(participantId, orderIds) {
    return await profileServiceRequest({
      method: "put",
      url: `/orientations/my_students/${participantId}`,
      skipErrorBus: true,
      payload: {
        orders_ids: orderIds,
      },
      failureMessage: "Error loading Profile Orientations",
    });
  },
  async createApplication(payload) {
    return await profileServiceRequest({
      method: "post",
      url: `/applications`,
      payload: payload,
    });
  },
  async updateApplication(applicationId, payload) {
    return await profileServiceRequest({
      method: "put",
      url: `/applications/${applicationId}`,
      payload: payload,
    });
  },
  async updateAcceptanceApplication({ applicationId, payload }) {
    return await profileServiceRequest({
      method: "patch",
      url: `/applications/${applicationId}/acceptance`,
      skipErrorBus: true,
      payload: payload,
      failureMessage: `Error updating acceptance for application id ${applicationId}`,
    });
  },

  async getApplicationById({ applicationId }) {
    return await profileServiceRequest({
      method: "get",
      url: `/applications/${applicationId}`,
      skipErrorBus: true,
      failureMessage: `Error getting application by id ${applicationId}`,
    });
  },

  async getApplicationsByParticipantId(participantId) {
    return await profileServiceRequest({
      method: "get",
      url: `/applications?participant_id=${participantId}`,
    });
  },

  /**
   * Retrieves the profile of a student participant according to My Students v2 access rules.
   * @param {string} participantId - The ID of the participant.
   * @returns {Promise<Profile>} A promise that resolves to the participant's profile.
   */
  async getMyStudentsProfile(participantId) {
    const response = await profileServiceRequest({
      method: "get",
      url: `/profiles/my_students_profile/${participantId}`,
      skipErrorBus: true,
      failureMessage: "Error loading My Students profile",
    });

    return response?.data?.data;
  },
  async generateReportToken(payload) {
    return await profileServiceRequest({
      method: "post",
      url: "/reports/token/",
      payload: payload,
    });
  },

  /**
   * Resolves the active impersonation token into the impersonated student's profile.
   * The token itself is opaque to the frontend and is never decoded client-side.
   * @returns {Promise<ResolvedImpersonation>}
   */
  async resolveImpersonation() {
    const response = await profileServiceRequest({
      method: "get",
      url: "/impersonation_token/resolve/",
      skipErrorBus: true,
      failureMessage: "Unable to resolve the impersonation session",
    });

    return response?.data?.data;
  },
};

export default profileService;
